'use client';
import { useCallback, useEffect, useRef } from 'react';
import type { Node } from '@xyflow/react';
import { useReactFlow, useNodesInitialized } from '@xyflow/react';
import dagre from '@dagrejs/dagre';
import {
  NODE_DEFAULT_HEIGHT,
  NODE_DEFAULT_WIDTH,
} from '../code-graph/constants';

export type Direction = 'TB' | 'LR' | 'RL' | 'BT';

export type LayoutOptions = {
  direction: Direction;
  nodeSeparation: number;
  rankSeparation: number;
  onLayoutComplete?: () => void;
};

const getNodeBounds = (node: Node) => {
  const width = node.width ?? node.measured?.width ?? NODE_DEFAULT_WIDTH;
  const height = node.height ?? node.measured?.height ?? NODE_DEFAULT_HEIGHT;
  return {
    left: node.position.x,
    right: node.position.x + width,
    top: node.position.y,
    bottom: node.position.y + height,
  };
};

const doNodesOverlap = (nodeA: Node, nodeB: Node, threshold = 0.5) => {
  const boundsA = getNodeBounds(nodeA);
  const boundsB = getNodeBounds(nodeB);

  const overlapX = Math.max(
    0,
    Math.min(boundsA.right, boundsB.right) -
      Math.max(boundsA.left, boundsB.left),
  );
  const overlapY = Math.max(
    0,
    Math.min(boundsA.bottom, boundsB.bottom) -
      Math.max(boundsA.top, boundsB.top),
  );

  if (overlapX === 0 || overlapY === 0) {
    return false;
  }

  const widthA = boundsA.right - boundsA.left;
  const heightA = boundsA.bottom - boundsA.top;
  const areaA = widthA * heightA;

  const overlapArea = overlapX * overlapY;
  const overlapRatio = overlapArea / areaA;

  return overlapRatio > threshold;
};

const hasOverlappingNodes = (nodes: Node[], threshold = 0.5) => {
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      if (doNodesOverlap(nodes[i], nodes[j], threshold)) {
        return true;
      }
    }
  }
  return false;
};

const MAX_REFRESH_ATTEMPTS = 10;
const REFRESH_INTERVAL = 100;

export function useAutoLayout(options: LayoutOptions) {
  const { setNodes, getNodes, getEdges } = useReactFlow();
  const nodesInitialized = useNodesInitialized();
  const prevNodeIdsRef = useRef('');
  const refreshAttemptsRef = useRef(0);
  const refreshLayoutTimerRef = useRef<NodeJS.Timeout | null>(null);

  const calculateLayout = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();

    if (nodes.length === 0) {
      return;
    }

    const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(
      () => ({}),
    );

    dagreGraph.setGraph({
      rankdir: options.direction,
      nodesep: options.nodeSeparation,
      ranksep: options.rankSeparation,
    });

    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, {
        width: node.width ?? node.measured?.width ?? NODE_DEFAULT_WIDTH,
        height: node.height ?? node.measured?.height ?? NODE_DEFAULT_HEIGHT,
      });
    });

    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: nodeWithPosition.x,
          y: nodeWithPosition.y,
        },
      };
    });

    setNodes(layoutedNodes);

    if (options.onLayoutComplete) {
      options.onLayoutComplete();
    }
  }, [getNodes, getEdges, setNodes, options]);

  const refreshLayoutIfNeeded = useCallback(() => {
    if (refreshLayoutTimerRef.current) {
      clearTimeout(refreshLayoutTimerRef.current);
    }

    refreshLayoutTimerRef.current = setTimeout(() => {
      const isNodesOverlapping = hasOverlappingNodes(getNodes());
      if (
        refreshAttemptsRef.current >= MAX_REFRESH_ATTEMPTS ||
        !isNodesOverlapping
      ) {
        return;
      }
      refreshAttemptsRef.current++;
      calculateLayout();
      refreshLayoutIfNeeded();
    }, REFRESH_INTERVAL);
  }, [calculateLayout, getNodes]);

  useEffect(() => {
    if (!nodesInitialized) {
      return;
    }

    const currentNodeIds = getNodes()
      .map((n) => n.id)
      .sort()
      .join(',');

    if (currentNodeIds === prevNodeIdsRef.current) {
      return;
    }

    prevNodeIdsRef.current = currentNodeIds;
    refreshAttemptsRef.current = 0;
    calculateLayout();
    refreshLayoutIfNeeded();
  }, [nodesInitialized, getNodes, calculateLayout, refreshLayoutIfNeeded]);

  return { refreshLayout: calculateLayout, hasOverlappingNodes };
}
